import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import Konva from 'konva';
import { v4 as uuidv4 } from 'uuid';

import * as models from 'app/models';
import * as services from 'app/services';
import { FloorPlanUploadComponent, NotesComponent } from 'app/dialogs';
import { DEFAULT_ZONE_COLOR, toLocalPoints, toNormalizedPoints, clamp01, isMappedOn } from './zone-map.util';

type Tool = 'select' | 'rect' | 'pin' | 'polygon' | 'wall' | 'label';

// One row in the side tree panel (flattened zone hierarchy).
interface ZoneRow {
  zone: models.Zone;
  depth: number;
  parentId: string;
}

// Full-page spatial editor: draw each zone's outline over an uploaded floor-plan
// image on a Konva stage, then persist normalized shapes back onto the zones
// document via ZoneService.update(). Additive — zones without mapData are
// untouched and the List view keeps working unchanged.
@Component({
  selector: 'app-zone-map-editor',
  templateUrl: './zone-map-editor.component.html',
  styleUrls: ['./zone-map-editor.component.scss'],
  standalone: false
})
export class ZoneMapEditorComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('stageHost', { static: false }) stageHost: ElementRef<HTMLDivElement>;

  // ---- data ----
  dbZone: models.DbZone;
  initialSnapshot: string;        // JSON of dbZone at last load/save (dirty check)
  initialFloorPlansSnapshot: string;
  loadedZoneRevision: number = 0;
  floorPlans: models.FloorPlan[] = [];
  sensors: models.Sensor[] = [];
  activeFloorPlan: models.FloorPlan;

  loading: boolean = true;
  saving: boolean = false;
  conflicted: boolean = false;
  showLabels: boolean = true;
  editingPlanElements: boolean = false;
  selectedPlanElementId: string;
  creatingZone: boolean = false;
  newZoneName: string = '';
  newZoneParentId: string = null;
  newZonePlacements: number = 1;
  zoneSearch: string = '';
  refreshingZones: boolean = false;
  draggingZoneId: string;
  pendingShape: models.ZoneShape;

  // ---- editor state ----
  tool: Tool = 'select';
  selectedZoneId: string;
  color: string = DEFAULT_ZONE_COLOR;
  levelVisible: boolean[] = [];   // per zoneTitles level
  focusZoneId: string;            // "entered" parent; null = top level

  // ---- Konva ----
  private stage: Konva.Stage;
  private content: Konva.Group;   // holds image + shapes, handles pan/zoom
  private bgLayer: Konva.Layer;
  private shapeLayer: Konva.Layer;
  private transformer: Konva.Transformer;
  private shapeNodes: { [zoneId: string]: Konva.Shape } = {};
  private vertexAnchors: Konva.Circle[] = [];
  private bgImageNode: Konva.Shape;
  private zoneLabelNodes: Konva.Text[] = [];
  private elementNodes: Konva.Node[] = [];

  // in-progress drawing
  private drawing: boolean = false;
  private draftRect: Konva.Rect;
  private draftPolyPoints: number[] = [];   // image-local
  private draftPolyLine: Konva.Line;
  private draftWallPoints: number[] = [];
  private draftWallLine: Konva.Line;

  private resizeObserver: ResizeObserver;
  private lastPinchDist: number = 0;

  constructor(
    private zoneService: services.ZoneService,
    private floorPlanService: services.FloorPlanService,
    private helperService: services.HelperService,
    private harvestService: services.HarvestService,
    private sensorService: services.SensorService,
    private leaflogixService: services.LeaflogixService,
    private notifier: services.NotifierService,
    private dialog: MatDialog,
    private zone: NgZone,
  ) { }

  // ---------------------------------------------------------------- lifecycle
  ngOnInit(): void {
    this.load();
  }

  ngAfterViewInit(): void {
    // stage is built once the floor plan image is ready (buildStage)
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.stage) this.stage.destroy();
  }

  get dirty(): boolean {
    return this.dbZone != null && (
      JSON.stringify(this.dbZone) !== this.initialSnapshot ||
      JSON.stringify(this.floorPlans) !== this.initialFloorPlansSnapshot
    );
  }

  get viewOnly(): boolean { return window.innerWidth < 1024; }
  get canSeeDutchie(): boolean { return !!this.helperService.currentCompany?.leaflogix?.active; }

  async load() {
    this.loading = true;
    let db = await this.zoneService.getZonesObject();
    this.dbZone = db || { zoneTitles: [], list: [] };
    if (this.dbZone.zoneTitles == null) this.dbZone.zoneTitles = [];
    if (this.dbZone.list == null) this.dbZone.list = [];
    this.dbZone.revision = this.validRevision(this.dbZone.revision);
    this.loadedZoneRevision = this.dbZone.revision;
    this.levelVisible = this.dbZone.zoneTitles.map(() => true);

    [this.floorPlans, this.sensors] = await Promise.all([
      this.floorPlanService.getAll(),
      this.sensorService.getAll(),
    ]);
    this.floorPlans = this.helperService.sortArrayByStringField(this.floorPlans, 'name');
    this.captureBaseline();
    this.conflicted = false;

    // pick the anchored plan for the first mapped zone, else the first plan
    this.activeFloorPlan = this.floorPlans[0];
    this.selectedZoneId = null;
    this.creatingZone = false;
    this.pendingShape = null;
    this.loading = false;

    if (this.activeFloorPlan) {
      await this.buildStage();
    } else if (this.stage) {
      this.stage.destroy();
      this.stage = null;
    }
  }

  // ------------------------------------------------------------ floor plans
  async selectFloorPlan(fp: models.FloorPlan) {
    if (this.activeFloorPlan && fp && this.activeFloorPlan.id === fp.id) return;
    this.activeFloorPlan = fp;
    this.selectedZoneId = null;
    await this.buildStage();
  }

  configureLevels() {
    let ref = this.dialog.open(NotesComponent, {
      width: '50%',
      data: { list: this.helperService.deepCopy(this.dbZone.zoneTitles || []), header: 'Zone Levels', placeholder: 'Level name' },
    });
    ref.afterClosed().subscribe((titles: string[]) => {
      if (!titles) return;
      let apply = () => {
        this.dbZone.zoneTitles = titles;
        this.trimZonesToDepth(this.dbZone.list, 0, titles.length);
        this.levelVisible = titles.map((_, index) => this.levelVisible[index] !== false);
      };
      if (titles.length < this.dbZone.zoneTitles.length && this.dbZone.list.length) {
        this.notifier.confirm(
          'Remove zone levels?',
          'Zones below the new final level will be removed when you save.',
          apply,
          () => { },
        );
      } else apply();
    });
  }

  async syncDutchie() {
    if (!this.requireCleanWorkspace('sync with Dutchie')) return;
    this.notifier.confirm('Sync zones with Dutchie?', 'Imported zones will be refreshed. Local map locations will be preserved.', async () => {
      this.refreshingZones = true;
      try {
        let retVal = await this.leaflogixService.integrateZones();
        let response: models.Result<boolean> = retVal.data;
        if (!response.success) throw new Error('Dutchie sync failed');
        await this.load();
        this.notifier.success('Zones synced with Dutchie');
      } catch (_) {
        this.notifier.error('Could not sync zones with Dutchie.');
      } finally {
        this.refreshingZones = false;
      }
    }, () => { });
  }

  floorPlanById(id: string): models.FloorPlan {
    return this.floorPlans.find(i => i.id === id);
  }

  mappedZoneCount(floorPlanId: string): number {
    return this.allZones.filter(zone => zone.mapData?.floorPlanId === floorPlanId && zone.mapData?.shape).length;
  }

  addFloorPlan() {
    if (!this.requireCleanWorkspace('add a floor plan')) return;
    let ref = this.dialog.open(FloorPlanUploadComponent, { data: {}, width: '520px' });
    ref.afterClosed().subscribe(async (fp: models.FloorPlan) => {
      if (fp) {
        this.floorPlans.push(fp);
        this.floorPlans = this.helperService.sortArrayByStringField(this.floorPlans, 'name');
        await this.selectFloorPlan(fp);
        this.captureBaseline();
      }
    });
  }

  editFloorPlan() {
    if (!this.activeFloorPlan) return;
    if (!this.requireCleanWorkspace('edit a floor plan')) return;
    let ref = this.dialog.open(FloorPlanUploadComponent, { data: { floorPlan: this.activeFloorPlan }, width: '520px' });
    ref.afterClosed().subscribe(async (fp: models.FloorPlan) => {
      if (fp) {
        let idx = this.floorPlans.findIndex(i => i.id === fp.id);
        if (idx > -1) this.floorPlans[idx] = fp;
        this.activeFloorPlan = fp;
        await this.buildStage();
        this.captureBaseline();
      }
    });
  }

  deleteFloorPlan() {
    if (!this.activeFloorPlan) return;
    if (!this.requireCleanWorkspace('delete a floor plan')) return;
    let fp = this.activeFloorPlan;
    this.notifier.confirm('Delete floor plan?',
      `Remove "${fp.name}"? Any zone shapes drawn on it will be cleared.`,
      async () => {
        // Clear mapData and remove the plan metadata in one Firestore transaction.
        this.walkZones(this.dbZone.list, z => {
          if (z.mapData && z.mapData.floorPlanId === fp.id) delete z.mapData;
        });
        try {
          let nextRevision = await this.zoneService.deleteFloorPlan(
            this.dbZone, this.loadedZoneRevision, fp.id, this.validRevision(fp.revision));
          this.dbZone.revision = nextRevision;
          this.loadedZoneRevision = nextRevision;
        } catch (e) {
          this.notifier.error('Could not delete the floor plan because the data changed or the request failed.');
          await this.load();
          return;
        }
        await this.floorPlanService.deleteStorageOnly(fp);
        this.floorPlans = this.floorPlans.filter(i => i.id !== fp.id);
        this.activeFloorPlan = this.floorPlans[0];
        if (this.activeFloorPlan) { await this.buildStage(); }
        else if (this.stage) { this.stage.destroy(); this.stage = null; }
        this.captureBaseline();
      }, () => { });
  }

  // ---------------------------------------------------------------- stage
  private async buildStage() {
    if (!this.stageHost) await this.helperService.wait(0);
    if (!this.stageHost || !this.activeFloorPlan) return;
    let url = await this.floorPlanService.getImageUrl(this.activeFloorPlan);
    let img: HTMLImageElement;
    if (url != null) {
      img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise<void>((res, rej) => { img.onload = () => res(); img.onerror = rej; img.src = url; });
    }

    this.zone.runOutsideAngular(() => {
      if (this.stage) { this.stage.destroy(); this.shapeNodes = {}; }

      let host = this.stageHost.nativeElement;
      this.stage = new Konva.Stage({ container: host, width: host.clientWidth, height: host.clientHeight });

      // Single layer holds everything. `shapeLayer` is an alias so every
      // batchDraw() targets the layer the shapes actually live in.
      this.bgLayer = new Konva.Layer();
      this.shapeLayer = this.bgLayer;
      this.stage.add(this.bgLayer);

      let iw = this.imgW;
      let ih = this.imgH;

      // content group works in image-pixel space; its own transform = pan/zoom
      this.content = new Konva.Group({
        draggable: true, clipX: 0, clipY: 0, clipWidth: iw, clipHeight: ih,
      });
      this.content.add(new Konva.Rect({ x: 0, y: 0, width: iw, height: ih, fill: '#fff', listening: false }));
      if (img) {
        let sourceW = img.naturalWidth || this.activeFloorPlan.imageWidth || iw;
        let sourceH = img.naturalHeight || this.activeFloorPlan.imageHeight || ih;
        let scale = this.activeFloorPlan.backgroundFit === 'fill'
          ? Math.max(iw / sourceW, ih / sourceH)
          : Math.min(iw / sourceW, ih / sourceH);
        let width = sourceW * scale, height = sourceH * scale;
        this.bgImageNode = new Konva.Image({
          image: img, width, height, x: (iw - width) / 2, y: (ih - height) / 2,
          listening: true,
        });
      } else {
        this.bgImageNode = new Konva.Rect({
          x: 0, y: 0, width: iw, height: ih, fill: '#fff', stroke: '#dfe6e1', strokeWidth: 1,
          listening: true,
        });
      }
      this.content.add(this.bgImageNode);
      this.bgLayer.add(this.content);

      // shapes live inside content so they share the pan/zoom transform
      let shapeGroup = new Konva.Group();
      this.content.add(shapeGroup);
      (this.content as any)._shapeGroup = shapeGroup;

      this.renderGridAndElements();

      // Transformer sits on the layer (not the scaled group) so its resize
      // handles stay a constant screen size regardless of zoom.
      this.transformer = new Konva.Transformer({
        rotateEnabled: false,
        ignoreStroke: true,
        borderStroke: '#2f9e54',
        anchorStroke: '#2f9e54',
        anchorFill: '#fff',
        anchorSize: 9,
      });
      this.bgLayer.add(this.transformer);

      this.fitToStage(iw, ih);
      this.wireStageEvents();
      this.renderAllShapes();
      this.bgLayer.draw();
    });

    this.observeResize();
  }

  private get shapeGroup(): Konva.Group {
    return (this.content as any)._shapeGroup as Konva.Group;
  }

  private fitToStage(iw: number, ih: number) {
    let sw = this.stage.width(), sh = this.stage.height();
    let scale = Math.min(sw / iw, sh / ih) * 0.95;
    this.content.scale({ x: scale, y: scale });
    this.content.position({ x: (sw - iw * scale) / 2, y: (sh - ih * scale) / 2 });
  }

  private observeResize() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    this.resizeObserver = new ResizeObserver(() => {
      if (!this.stage) return;
      let host = this.stageHost.nativeElement;
      this.stage.width(host.clientWidth);
      this.stage.height(host.clientHeight);
      this.stage.batchDraw();
    });
    this.resizeObserver.observe(this.stageHost.nativeElement);
  }

  // ------------------------------------------------------------ events
  private wireStageEvents() {
    // wheel zoom around pointer
    this.stage.on('wheel', (e) => {
      e.evt.preventDefault();
      let oldScale = this.content.scaleX();
      let pointer = this.stage.getPointerPosition();
      let mousePoint = {
        x: (pointer.x - this.content.x()) / oldScale,
        y: (pointer.y - this.content.y()) / oldScale,
      };
      let direction = e.evt.deltaY > 0 ? -1 : 1;
      let newScale = direction > 0 ? oldScale * 1.08 : oldScale / 1.08;
      newScale = Math.max(0.05, Math.min(20, newScale));
      this.content.scale({ x: newScale, y: newScale });
      this.content.position({
        x: pointer.x - mousePoint.x * newScale,
        y: pointer.y - mousePoint.y * newScale,
      });
      this.stage.batchDraw();
    });

    // two-finger pinch zoom (tablets)
    this.stage.on('touchmove', (e) => {
      let t = (e.evt as TouchEvent).touches;
      if (t.length !== 2) return;
      e.evt.preventDefault();
      this.content.draggable(false);
      let p1 = { x: t[0].clientX, y: t[0].clientY };
      let p2 = { x: t[1].clientX, y: t[1].clientY };
      let dist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
      let center = this.stagePointFromClient((p1.x + p2.x) / 2, (p1.y + p2.y) / 2);
      if (this.lastPinchDist) {
        let oldScale = this.content.scaleX();
        let local = {
          x: (center.x - this.content.x()) / oldScale,
          y: (center.y - this.content.y()) / oldScale,
        };
        let newScale = Math.max(0.05, Math.min(20, oldScale * (dist / this.lastPinchDist)));
        this.content.scale({ x: newScale, y: newScale });
        this.content.position({ x: center.x - local.x * newScale, y: center.y - local.y * newScale });
        this.stage.batchDraw();
      }
      this.lastPinchDist = dist;
    });
    this.stage.on('touchend', () => { this.lastPinchDist = 0; this.content.draggable(this.tool === 'select'); });

    // drawing / selection start
    this.stage.on('mousedown touchstart', (e) => {
      // in a drawing tool, only start when pressing on the image itself
      if (this.tool === 'rect') { this.startRect(); }
      else if (this.tool === 'pin') { this.placePin(); }
      else if (this.tool === 'polygon') { this.polygonClick(); }
      else if (this.tool === 'wall') { this.wallClick(); }
      else if (this.tool === 'label') { this.labelClick(); }
      else {
        // select tool: clicking empty background clears selection
        if (e.target === this.bgImageNode || e.target === this.stage) {
          this.setSelected(null);
        }
      }
    });

    this.stage.on('mousemove touchmove', () => {
      if (this.drawing && this.tool === 'rect') this.updateRect();
    });

    this.stage.on('mouseup touchend', () => {
      if (this.drawing && this.tool === 'rect') this.finishRect();
    });

    // double-click a shape to "enter" that zone (edit its children)
    this.stage.on('dblclick dbltap', (e) => {
      let id = e.target && (e.target as any).zoneId;
      if (id) this.zone.run(() => this.enterZone(id));
    });
  }

  // client (viewport) px -> stage-container px
  private stagePointFromClient(cx: number, cy: number): { x: number; y: number } {
    let box = this.stage.container().getBoundingClientRect();
    return { x: cx - box.left, y: cy - box.top };
  }

  private localPointer(): { x: number; y: number } {
    // pointer in image-local (content) coordinates
    let p = this.content.getRelativePointerPosition();
    return { x: p.x, y: p.y };
  }

  // ------------------------------------------------------------ rect tool
  private startRect() {
    let p = this.localPointer();
    this.drawing = true;
    this.draftRect = new Konva.Rect({
      x: p.x, y: p.y, width: 0, height: 0,
      fill: this.hexA(this.color, 0.28), stroke: this.color, strokeWidth: 2, strokeScaleEnabled: false,
    });
    this.shapeGroup.add(this.draftRect);
  }

  private updateRect() {
    if (!this.draftRect) return;
    let p = this.localPointer();
    this.draftRect.width(p.x - this.draftRect.x());
    this.draftRect.height(p.y - this.draftRect.y());
    this.shapeLayer.batchDraw();
  }

  private finishRect() {
    this.drawing = false;
    let r = this.draftRect;
    this.draftRect = null;
    if (!r) return;
    // normalize negative sizes
    let x = r.width() < 0 ? r.x() + r.width() : r.x();
    let y = r.height() < 0 ? r.y() + r.height() : r.y();
    let w = Math.abs(r.width()), h = Math.abs(r.height());
    r.destroy();
    if (w < 4 || h < 4) { this.shapeLayer.batchDraw(); return; } // ignore stray clicks

    let iw = this.imgW, ih = this.imgH;
    let norm = [clamp01(x / iw), clamp01(y / ih), clamp01(w / iw), clamp01(h / ih)];
    this.zone.run(() => this.commitShapeToSelectedOrPrompt('rect', norm));
  }

  // ------------------------------------------------------------ pin tool
  private placePin() {
    let p = this.localPointer();
    let norm = [clamp01(p.x / this.imgW), clamp01(p.y / this.imgH)];
    this.zone.run(() => this.commitShapeToSelectedOrPrompt('pin', norm));
  }

  // ------------------------------------------------------------ polygon tool
  private polygonClick() {
    let p = this.localPointer();
    // close when clicking near the first vertex
    if (this.draftPolyPoints.length >= 6) {
      let dx = p.x - this.draftPolyPoints[0], dy = p.y - this.draftPolyPoints[1];
      let distPx = Math.hypot(dx, dy) * this.content.scaleX();
      if (distPx < 12) { this.finishPolygon(); return; }
    }
    this.draftPolyPoints.push(p.x, p.y);
    if (!this.draftPolyLine) {
      this.draftPolyLine = new Konva.Line({
        points: this.draftPolyPoints, stroke: this.color, strokeWidth: 2, strokeScaleEnabled: false,
        closed: false, fill: this.hexA(this.color, 0.2),
      });
      this.shapeGroup.add(this.draftPolyLine);
    } else {
      this.draftPolyLine.points(this.draftPolyPoints);
    }
    this.shapeLayer.batchDraw();
  }

  finishPolygon() {
    if (this.draftPolyPoints.length < 6) { this.cancelPolygon(); return; }
    let norm = toNormalizedPoints(this.draftPolyPoints, this.imgW, this.imgH);
    this.cancelPolygon();
    this.zone.run(() => this.commitShapeToSelectedOrPrompt('polygon', norm));
  }

  cancelPolygon() {
    this.draftPolyPoints = [];
    if (this.draftPolyLine) { this.draftPolyLine.destroy(); this.draftPolyLine = null; }
    this.shapeLayer.batchDraw();
  }

  private wallClick() {
    let p = this.localPointer();
    this.draftWallPoints.push(p.x, p.y);
    if (!this.draftWallLine) {
      this.draftWallLine = new Konva.Line({
        points: this.draftWallPoints, stroke: '#6b7670', strokeWidth: 3,
        strokeScaleEnabled: false, lineCap: 'round', lineJoin: 'round',
      });
      this.shapeGroup.add(this.draftWallLine);
    } else {
      this.draftWallLine.points(this.draftWallPoints);
    }
    this.shapeLayer.batchDraw();
  }

  finishWall() {
    if (this.draftWallPoints.length < 4) { this.cancelWall(); return; }
    this.activeFloorPlan.elements = this.activeFloorPlan.elements || [];
    this.activeFloorPlan.elements.push({
      id: uuidv4(), type: 'line',
      points: toNormalizedPoints(this.draftWallPoints, this.imgW, this.imgH),
    });
    this.cancelWall();
    this.setTool('select');
    this.renderGridAndElements();
    this.renderAllShapes();
  }

  cancelWall() {
    this.draftWallPoints = [];
    if (this.draftWallLine) { this.draftWallLine.destroy(); this.draftWallLine = null; }
    if (this.shapeLayer) this.shapeLayer.batchDraw();
  }

  private labelClick() {
    let p = this.localPointer();
    let value = window.prompt('Label text');
    if (!value || !value.trim()) return;
    this.activeFloorPlan.elements = this.activeFloorPlan.elements || [];
    this.activeFloorPlan.elements.push({
      id: uuidv4(), type: 'label', text: value.trim(),
      x: clamp01(p.x / this.imgW), y: clamp01(p.y / this.imgH),
    });
    this.setTool('select');
    this.renderGridAndElements();
    this.renderAllShapes();
  }

  // ------------------------------------------------------------ commit shape
  // Drawing without a selected zone starts the draw-first creation flow.
  private commitShapeToSelectedOrPrompt(type: models.ZoneShapeType, points: number[]) {
    let targetId = this.selectedZoneId || this.pendingZoneId;
    if (!targetId) {
      this.pendingShape = { type, points };
      this.startCreateZone(this.focusZoneId);
      return;
    }
    let targetZone = this.zoneService.getZone(this.dbZone.list, targetId);
    if (!targetZone) return;
    if (targetZone.mapData && targetZone.mapData.shape) {
      this.notifier.confirm(
        'Replace zone shape?',
        `Replace the existing map location for "${targetZone.name}"?`,
        () => this.applyShape(targetZone, type, points),
        () => this.renderAllShapes(),
      );
    } else {
      this.applyShape(targetZone, type, points);
    }
  }

  pendingZoneId: string;

  private applyShape(targetZone: models.Zone, type: models.ZoneShapeType, points: number[]) {
    targetZone.mapData = {
      floorPlanId: this.activeFloorPlan.id,
      color: this.color,
      shape: { type, points },
    };
    this.pendingZoneId = null;
    this.tool = 'select';
    this.renderAllShapes();
    this.setSelected(targetZone.id);
  }

  // ------------------------------------------------------------ render shapes
  private renderAllShapes() {
    if (!this.activeFloorPlan || !this.shapeGroup || !this.transformer) return;
    this.zone.runOutsideAngular(() => {
      this.clearVertexAnchors();
      // remove existing shape nodes (keep transformer)
      Object.values(this.shapeNodes).forEach(n => n.destroy());
      this.shapeNodes = {};
      this.zoneLabelNodes.forEach(n => n.destroy());
      this.zoneLabelNodes = [];

      this.walkZones(this.dbZone.list, (z) => {
        if (!isMappedOn(z, this.activeFloorPlan.id)) return;
        if (!this.isZoneVisible(z)) return;
        let node = this.buildShapeNode(z);
        if (node) {
          this.shapeNodes[z.id] = node;
          this.shapeGroup.add(node);
          if (this.showLabels) this.addZoneLabel(z, node);
        }
      });
      this.transformer.moveToTop();
      this.applySelectionVisual();
      this.shapeLayer.batchDraw();
    });
  }

  private buildShapeNode(zone: models.Zone): Konva.Shape {
    let s = zone.mapData.shape;
    let color = zone.mapData.color || DEFAULT_ZONE_COLOR;
    let iw = this.imgW, ih = this.imgH;
    let node: Konva.Shape;

    if (s.type === 'rect') {
      let [x, y, w, h] = toLocalPoints(s.points, iw, ih);
      node = new Konva.Rect({
        x, y, width: w, height: h,
        fill: this.hexA(color, 0.28), stroke: color, strokeWidth: 2, strokeScaleEnabled: false,
        draggable: !this.viewOnly,
      });
      node.on('transformend dragend', () => this.onRectChanged(zone.id, node as Konva.Rect));
    } else if (s.type === 'pin') {
      let [x, y] = toLocalPoints(s.points, iw, ih);
      // pin size is in image units, scaled visually by content scale
      let r = Math.max(iw, ih) * 0.012;
      node = new Konva.Circle({
        x, y, radius: r, fill: color, stroke: '#fff', strokeWidth: r * 0.25, strokeScaleEnabled: true,
        draggable: !this.viewOnly,
      });
      node.on('dragend', () => this.onPinChanged(zone.id, node as Konva.Circle));
    } else {
      let pts = toLocalPoints(s.points, iw, ih);
      node = new Konva.Line({
        points: pts, closed: true,
        fill: this.hexA(color, 0.28), stroke: color, strokeWidth: 2, strokeScaleEnabled: false,
        draggable: !this.viewOnly,
      });
      node.on('dragend', () => this.onPolygonDragged(zone.id, node as Konva.Line));
    }

    (node as any).zoneId = zone.id;
    node.on('mousedown touchstart', (e) => {
      if (this.tool === 'select') {
        e.cancelBubble = true;
        this.zone.run(() => this.setSelected(zone.id));
      }
    });
    return node;
  }

  private addZoneLabel(zone: models.Zone, node: Konva.Shape) {
    let bounds = node.getClientRect({ relativeTo: this.shapeGroup });
    let label = new Konva.Text({
      x: bounds.x, y: bounds.y + bounds.height / 2 - 8,
      width: Math.max(bounds.width, 80), text: zone.name || '', align: 'center',
      fontSize: Math.max(this.imgW, this.imgH) * 0.012,
      fontFamily: 'Outfit, Helvetica, sans-serif', fill: '#1d2a22', listening: false,
    });
    if (zone.mapData.shape.type === 'pin') {
      label.x(bounds.x + bounds.width + 4);
      label.y(bounds.y);
      label.width(Math.max(this.imgW * .16, 100));
      label.align('left');
    }
    this.zoneLabelNodes.push(label);
    (label as any).zoneId = zone.id;
    this.shapeGroup.add(label);
  }

  // ------------------------------------------------------------ edits -> model
  private onRectChanged(zoneId: string, node: Konva.Rect) {
    // fold scale into width/height
    let w = node.width() * node.scaleX();
    let h = node.height() * node.scaleY();
    node.scale({ x: 1, y: 1 });
    node.width(w); node.height(h);
    let norm = [
      clamp01(node.x() / this.imgW), clamp01(node.y() / this.imgH),
      clamp01(w / this.imgW), clamp01(h / this.imgH),
    ];
    this.updateZoneShape(zoneId, 'rect', norm);
  }

  private onPinChanged(zoneId: string, node: Konva.Circle) {
    let norm = [clamp01(node.x() / this.imgW), clamp01(node.y() / this.imgH)];
    this.updateZoneShape(zoneId, 'pin', norm);
  }

  private onPolygonDragged(zoneId: string, node: Konva.Line) {
    // bake the drag offset into the points, then reset node position
    let pts = node.points();
    let dx = node.x(), dy = node.y();
    let moved: number[] = [];
    for (let i = 0; i < pts.length; i += 2) moved.push(pts[i] + dx, pts[i + 1] + dy);
    node.position({ x: 0, y: 0 });
    node.points(moved);
    this.updateZoneShape(zoneId, 'polygon', toNormalizedPoints(moved, this.imgW, this.imgH));
  }

  private updateZoneShape(zoneId: string, type: models.ZoneShapeType, points: number[]) {
    let z = this.zoneService.getZone(this.dbZone.list, zoneId);
    if (!z || !z.mapData) return;
    z.mapData.shape = { type, points };
    this.zone.run(() => { /* trigger CD for dirty flag */ });
  }

  // ------------------------------------------------------------ selection
  setSelected(zoneId: string) {
    this.selectedZoneId = zoneId;
    let z = zoneId ? this.zoneService.getZone(this.dbZone.list, zoneId) : null;
    if (z && z.mapData && z.mapData.color) this.color = z.mapData.color;
    this.zone.runOutsideAngular(() => this.applySelectionVisual());
  }

  private applySelectionVisual() {
    if (!this.transformer) return;
    this.clearVertexAnchors();
    let node = this.selectedZoneId ? this.shapeNodes[this.selectedZoneId] : null;

    Object.keys(this.shapeNodes).forEach(id => this.shapeNodes[id].opacity(this.zoneOpacity(id)));
    this.zoneLabelNodes.forEach(label => label.opacity(this.zoneOpacity((label as any).zoneId)));

    if (!this.viewOnly && node && node.getClassName() === 'Rect') {
      this.transformer.nodes([node]);
    } else {
      this.transformer.nodes([]);
    }
    if (!this.viewOnly && node && node.getClassName() === 'Line') {
      this.buildVertexAnchors(this.selectedZoneId, node as Konva.Line);
    }
    this.shapeLayer.batchDraw();
  }

  private zoneOpacity(zoneId: string): number {
    if (!this.selectedZoneId || zoneId === this.selectedZoneId) return 1;
    let selected = this.selectedZone;
    if (!selected) return 1;
    let selectedParents = this.zoneService.getListOfParents(this.dbZone.list, this.selectedZoneId);
    let selectedDescendants = this.descendantIds(selected);
    return selectedParents.includes(zoneId) || selectedDescendants.includes(zoneId) ? .8 : .22;
  }

  // draggable per-vertex handles for polygon editing (Phase 2)
  private buildVertexAnchors(zoneId: string, line: Konva.Line) {
    let pts = line.points();
    let scale = this.content.scaleX();
    for (let i = 0; i < pts.length; i += 2) {
      let idx = i;
      let anchor = new Konva.Circle({
        x: pts[i], y: pts[i + 1], radius: 6 / scale,
        fill: '#fff', stroke: '#2f9e54', strokeWidth: 2 / scale, draggable: true,
      });
      anchor.on('dragmove', () => {
        let p = line.points();
        p[idx] = anchor.x(); p[idx + 1] = anchor.y();
        line.points(p);
        this.shapeLayer.batchDraw();
      });
      anchor.on('dragend', () => {
        this.updateZoneShape(zoneId, 'polygon', toNormalizedPoints(line.points(), this.imgW, this.imgH));
      });
      this.vertexAnchors.push(anchor);
      this.shapeGroup.add(anchor);
    }
  }

  private clearVertexAnchors() {
    this.vertexAnchors.forEach(a => a.destroy());
    this.vertexAnchors = [];
  }

  private renderGridAndElements() {
    if (!this.shapeGroup || !this.activeFloorPlan) return;
    this.elementNodes.forEach(node => node.destroy());
    this.elementNodes = [];

    if (this.activeFloorPlan.showGrid && !this.activeFloorPlan.fullPath) {
      let spacing = Math.max(this.imgW, this.imgH) / 20;
      for (let x = spacing; x < this.imgW; x += spacing) {
        this.addPlanNode(new Konva.Line({
          points: [x, 0, x, this.imgH], stroke: '#eef1ef', strokeWidth: 1, listening: false,
        }));
      }
      for (let y = spacing; y < this.imgH; y += spacing) {
        this.addPlanNode(new Konva.Line({
          points: [0, y, this.imgW, y], stroke: '#eef1ef', strokeWidth: 1, listening: false,
        }));
      }
    }

    (this.activeFloorPlan.elements || []).forEach(element => {
      let node: Konva.Shape;
      if (element.type === 'line') {
        let line = new Konva.Line({
          points: toLocalPoints(element.points || [], this.imgW, this.imgH),
          stroke: '#6b7670', strokeWidth: 3, strokeScaleEnabled: false,
          lineCap: 'round', lineJoin: 'round', draggable: this.editingPlanElements,
        });
        line.on('dragend', () => {
          let moved = line.points().map((value, index) => value + (index % 2 === 0 ? line.x() : line.y()));
          line.position({ x: 0, y: 0 });
          line.points(moved);
          element.points = toNormalizedPoints(moved, this.imgW, this.imgH);
          this.zone.run(() => { });
        });
        node = line;
      } else {
        let label = new Konva.Text({
          x: (element.x || 0) * this.imgW, y: (element.y || 0) * this.imgH,
          text: element.text || '', fontSize: Math.max(this.imgW, this.imgH) * .014,
          fontFamily: 'Outfit, Helvetica, sans-serif', fill: '#59635d',
          draggable: this.editingPlanElements,
        });
        label.on('dragend', () => {
          element.x = clamp01(label.x() / this.imgW);
          element.y = clamp01(label.y() / this.imgH);
          this.zone.run(() => { });
        });
        node = label;
      }
      (node as any).planElementId = element.id;
      node.on('mousedown touchstart', e => {
        if (!this.editingPlanElements) return;
        e.cancelBubble = true;
        this.zone.run(() => this.selectedPlanElementId = element.id);
      });
      this.addPlanNode(node);
    });
    this.shapeLayer.batchDraw();
  }

  private addPlanNode(node: Konva.Shape) {
    this.elementNodes.push(node);
    this.shapeGroup.add(node);
  }

  togglePlanEditing() {
    this.editingPlanElements = !this.editingPlanElements;
    this.setTool('select');
    this.renderGridAndElements();
    this.renderAllShapes();
  }

  removeSelectedPlanElement() {
    if (!this.selectedPlanElementId || !this.activeFloorPlan) return;
    this.activeFloorPlan.elements = (this.activeFloorPlan.elements || [])
      .filter(element => element.id !== this.selectedPlanElementId);
    this.selectedPlanElementId = null;
    this.renderGridAndElements();
    this.renderAllShapes();
  }

  // ------------------------------------------------------------ tree panel
  get selectedZone(): models.Zone {
    return this.selectedZoneId ? this.zoneService.getZone(this.dbZone.list, this.selectedZoneId) : null;
  }

  get canCreateRootZone(): boolean {
    return (this.dbZone.zoneTitles || []).length > 0;
  }

  get createParentOptions(): models.Zone[] {
    let maxParentDepth = (this.dbZone.zoneTitles || []).length - 2;
    return this.allZones.filter(zone => this.depthOf(zone.id) <= maxParentDepth);
  }

  get selectedParentOptions(): models.Zone[] {
    if (!this.selectedZone) return [];
    let expectedParentDepth = this.depthOf(this.selectedZone.id) - 1;
    if (expectedParentDepth < 0) return [];
    let descendants = new Set(this.descendantIds(this.selectedZone));
    return this.allZones.filter(zone =>
      this.depthOf(zone.id) === expectedParentDepth &&
      zone.id !== this.selectedZone.id && !descendants.has(zone.id));
  }

  get selectedParentId(): string {
    if (!this.selectedZone) return null;
    let parents = this.zoneService.getListOfParents(this.dbZone.list, this.selectedZone.id);
    return parents.length ? parents[parents.length - 1] : null;
  }

  set selectedParentId(parentId: string) {
    if (!this.selectedZone || parentId === this.selectedParentId) return;
    this.moveZone(this.selectedZone.id, parentId || null);
  }

  get selectedCanEditPlacements(): boolean {
    return !!this.selectedZone && (!this.selectedZone.zones || this.selectedZone.zones.length === 0);
  }

  get selectedSensors(): models.Sensor[] {
    return this.selectedZone ? this.sensors.filter(sensor => sensor.zoneId === this.selectedZone.id) : [];
  }

  get canAddChildSelected(): boolean {
    return !!this.selectedZone && this.depthOf(this.selectedZone.id) < (this.dbZone.zoneTitles || []).length - 1;
  }

  get allZones(): models.Zone[] {
    let zones: models.Zone[] = [];
    this.walkZones(this.dbZone?.list || [], zone => zones.push(zone));
    return zones;
  }

  startCreateZone(parentId: string = null) {
    if (!this.canCreateRootZone) {
      this.notifier.error('Configure at least one zone level before adding zones.');
      return;
    }
    let parent = parentId ? this.zoneService.getZone(this.dbZone.list, parentId) : null;
    let maxParentDepth = this.dbZone.zoneTitles.length - 2;
    this.newZoneParentId = parent && this.depthOf(parent.id) <= maxParentDepth ? parent.id : null;
    this.newZoneName = '';
    this.newZonePlacements = 1;
    this.creatingZone = true;
    this.selectedZoneId = null;
    this.pendingZoneId = null;
  }

  createZone() {
    let name = (this.newZoneName || '').trim();
    if (!name) { this.notifier.error('Enter a zone name.'); return; }
    let targetList = this.newZoneParentId
      ? this.zoneService.getZone(this.dbZone.list, this.newZoneParentId)?.zones
      : this.dbZone.list;
    if (!targetList) { this.notifier.error('Choose a valid parent zone.'); return; }
    if (targetList.some(zone => (zone.name || '').trim().toLowerCase() === name.toLowerCase())) {
      this.notifier.error('Zone names must be unique within the same parent.');
      return;
    }
    let zone: models.Zone = {
      id: uuidv4(), name, placements: Math.max(0, Number(this.newZonePlacements) || 0), zones: [],
    };
    if (this.pendingShape) {
      zone.mapData = {
        floorPlanId: this.activeFloorPlan.id,
        color: this.color,
        shape: this.pendingShape,
      };
    }
    targetList.push(zone);
    this.cancelCreateZone(false);
    this.setSelected(zone.id);
    this.setTool('select');
    this.renderAllShapes();
  }

  cancelCreateZone(redraw: boolean = true) {
    this.creatingZone = false;
    this.pendingShape = null;
    this.newZoneName = '';
    if (redraw && this.stage) this.renderAllShapes();
  }

  moveSelectedZone(parentId: string) {
    if (this.selectedZone) this.moveZone(this.selectedZone.id, parentId || null);
  }

  startZoneDrag(zoneId: string, event: DragEvent) {
    if (this.viewOnly) return;
    this.draggingZoneId = zoneId;
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
  }

  canDropZoneOn(parentId: string): boolean {
    if (!this.draggingZoneId || this.draggingZoneId === parentId) return false;
    let dragged = this.zoneService.getZone(this.dbZone.list, this.draggingZoneId);
    let parent = this.zoneService.getZone(this.dbZone.list, parentId);
    if (!dragged || !parent) return false;
    return this.depthOf(parentId) === this.depthOf(this.draggingZoneId) - 1 &&
      !this.descendantIds(dragged).includes(parentId);
  }

  dropZoneOn(parentId: string, event: DragEvent) {
    event.preventDefault();
    if (this.canDropZoneOn(parentId)) this.moveZone(this.draggingZoneId, parentId);
    this.draggingZoneId = null;
  }

  endZoneDrag() { this.draggingZoneId = null; }

  private moveZone(zoneId: string, newParentId: string) {
    let zone = this.zoneService.getZone(this.dbZone.list, zoneId);
    if (!zone) return;
    let currentParents = this.zoneService.getListOfParents(this.dbZone.list, zoneId);
    let currentList = currentParents.length
      ? this.zoneService.getZone(this.dbZone.list, currentParents[currentParents.length - 1]).zones
      : this.dbZone.list;
    let targetList = newParentId
      ? this.zoneService.getZone(this.dbZone.list, newParentId)?.zones
      : this.dbZone.list;
    if (!targetList) return;
    let index = currentList.findIndex(item => item.id === zoneId);
    if (index < 0) return;
    currentList.splice(index, 1);
    targetList.push(zone);
  }

  async deleteSelectedZone() {
    let selected = this.selectedZone;
    if (!selected) return;
    let ids = [selected.id, ...this.descendantIds(selected)];
    try {
      let [sensors, activeHarvests, allHarvests] = await Promise.all([
        this.sensorService.getAll(),
        this.harvestService.getHarvestsAndPhasesByZoneIds(ids),
        this.harvestService.getAllModels(false),
      ]);
      let linkedSensors = sensors.filter(sensor => ids.includes(sensor.zoneId));
      if (linkedSensors.length || activeHarvests.length) {
        this.notifier.error(`This zone cannot be deleted: ${linkedSensors.length} sensor(s) and ${activeHarvests.length} active harvest(s) still use it.`);
        return;
      }
      let historicalCount = allHarvests.filter(harvest => harvest.completed && this.harvestUsesAnyZone(harvest, ids)).length;
      let message = `Delete "${selected.name}" and ${ids.length - 1} child zone(s)?`;
      if (historicalCount) message += ` ${historicalCount} completed harvest(s) retain historical references.`;
      this.notifier.confirm('Delete zone?', message, () => this.removeZone(selected.id), () => { });
    } catch (_) {
      this.notifier.error('Could not verify zone usage. The zone was not deleted.');
    }
  }

  copySelectedZone() {
    let selected = this.selectedZone;
    if (!selected) return;
    let copy: models.Zone = this.helperService.deepCopy(selected);
    copy.name = `${copy.name} Copy`;
    this.regenerateZoneIds(copy);
    let parents = this.zoneService.getListOfParents(this.dbZone.list, selected.id);
    let list = parents.length
      ? this.zoneService.getZone(this.dbZone.list, parents[parents.length - 1]).zones
      : this.dbZone.list;
    list.push(copy);
    this.setSelected(copy.id);
    this.renderAllShapes();
  }

  private regenerateZoneIds(zone: models.Zone) {
    zone.id = uuidv4();
    delete zone.mapData;
    (zone.zones || []).forEach(child => this.regenerateZoneIds(child));
  }

  private harvestUsesAnyZone(harvest: models.Harvest, ids: string[]): boolean {
    return (harvest.phases || []).some(phase => (phase.strains || []).some(strain =>
      (strain.zones || []).some(zone => ids.includes(zone.zoneId))));
  }

  private removeZone(id: string) {
    let parents = this.zoneService.getListOfParents(this.dbZone.list, id);
    let list = parents.length
      ? this.zoneService.getZone(this.dbZone.list, parents[parents.length - 1]).zones
      : this.dbZone.list;
    let index = list.findIndex(zone => zone.id === id);
    if (index > -1) list.splice(index, 1);
    this.setSelected(null);
    this.renderAllShapes();
  }

  private descendantIds(zone: models.Zone): string[] {
    let ids: string[] = [];
    this.walkZones(zone.zones || [], child => ids.push(child.id));
    return ids;
  }
  get zoneRows(): ZoneRow[] {
    let rows: ZoneRow[] = [];
    let roots = this.focusZoneId
      ? (this.zoneService.getZone(this.dbZone.list, this.focusZoneId)?.zones || [])
      : this.dbZone.list;
    let baseDepth = this.focusZoneId ? this.depthOf(this.focusZoneId) + 1 : 0;
    this.flatten(roots, baseDepth, this.focusZoneId, rows);
    let query = (this.zoneSearch || '').trim().toLowerCase();
    return query ? rows.filter(row => (row.zone.name || '').toLowerCase().includes(query)) : rows;
  }

  private flatten(list: models.Zone[], depth: number, parentId: string, out: ZoneRow[]) {
    let sorted = this.helperService.sortArrayByStringField(list || [], 'name');
    sorted.forEach(z => {
      out.push({ zone: z, depth, parentId });
      if (z.zones && z.zones.length) this.flatten(z.zones, depth + 1, z.id, out);
    });
  }

  private depthOf(id: string): number {
    return this.zoneService.getListOfParents(this.dbZone.list, id).length;
  }

  isMapped(zone: models.Zone): boolean {
    return !!zone?.mapData?.floorPlanId && !!zone?.mapData?.shape;
  }

  async rowClick(row: ZoneRow) {
    if (this.isMapped(row.zone)) {
      if (row.zone.mapData.floorPlanId !== this.activeFloorPlan?.id) {
        let mappedPlan = this.floorPlanById(row.zone.mapData.floorPlanId);
        if (mappedPlan) await this.selectFloorPlan(mappedPlan);
      }
      this.setSelected(row.zone.id);
      this.focusShape(row.zone.id);
    } else {
      // arm this zone as the draw target
      this.pendingZoneId = row.zone.id;
      this.selectedZoneId = row.zone.id;
      if (row.zone.mapData && row.zone.mapData.color) this.color = row.zone.mapData.color;
    }
  }

  enterZone(id: string) {
    this.focusZoneId = id;
    this.setSelected(id);
  }

  exitZone() {
    this.focusZoneId = this.focusZoneId ? (this.zoneService.getListOfParents(this.dbZone.list, this.focusZoneId).pop() || null) : null;
  }

  get focusZoneName(): string {
    if (!this.focusZoneId) return null;
    let z = this.zoneService.getZone(this.dbZone.list, this.focusZoneId);
    return z ? z.name : null;
  }

  private focusShape(zoneId: string) {
    let node = this.shapeNodes[zoneId];
    if (!node || !this.stage) return;
    // no camera move; just flash selection. (kept simple)
    this.applySelectionVisual();
  }

  // ------------------------------------------------------------ visibility
  isZoneVisible(zone: models.Zone): boolean {
    let depth = this.depthOf(zone.id);
    if (this.levelVisible[depth] === false) return false;
    return true;
  }

  toggleLevel(i: number) {
    this.levelVisible[i] = !this.levelVisible[i];
    this.renderAllShapes();
  }

  // ------------------------------------------------------------ tools/color
  setTool(t: Tool) {
    this.tool = t;
    if (t !== 'polygon') this.cancelPolygon();
    if (t !== 'wall') this.cancelWall();
    // In a drawing tool the background must not pan (the drag draws the shape);
    // in select mode dragging the empty background pans the view.
    if (this.content) this.content.draggable(t === 'select');
    if (t !== 'select') { this.setSelected(this.selectedZoneId); } // keep target armed
  }

  toggleLabels() {
    this.showLabels = !this.showLabels;
    this.renderAllShapes();
  }

  onColorChange(c: string) {
    this.color = c;
    if (this.selectedZoneId) {
      let z = this.zoneService.getZone(this.dbZone.list, this.selectedZoneId);
      if (z && z.mapData) {
        z.mapData.color = c;
        this.renderAllShapes();
        this.applySelectionVisual();
      }
    }
  }

  clearSelectedShape() {
    if (!this.selectedZoneId) return;
    let z = this.zoneService.getZone(this.dbZone.list, this.selectedZoneId);
    if (z && z.mapData) { delete z.mapData; }
    this.setSelected(null);
    this.renderAllShapes();
  }

  resetView() {
    if (this.activeFloorPlan && this.stage) {
      this.zone.runOutsideAngular(() => { this.fitToStage(this.imgW, this.imgH); this.stage.batchDraw(); });
    }
  }

  // ------------------------------------------------------------ save
  async save() {
    if (!this.dirty || this.saving) return;
    if (this.creatingZone) {
      this.notifier.error('Finish or cancel the zone you are creating before saving.');
      return;
    }
    let validationError = this.validateZoneList(this.dbZone.list || [], 0);
    if (validationError) {
      this.notifier.error(validationError);
      return;
    }
    this.saving = true;
    try {
      this.setZonePlacements(this.dbZone.list);
      let initialPlans: models.FloorPlan[] = JSON.parse(this.initialFloorPlansSnapshot || '[]');
      let changes = this.floorPlans
        .filter(plan => JSON.stringify(plan) !== JSON.stringify(initialPlans.find(initial => initial.id === plan.id)))
        .map(plan => ({ floorPlan: plan, expectedRevision: this.validRevision(plan.revision) }));
      let result = await this.zoneService.saveWorkspace(this.dbZone, this.loadedZoneRevision, changes);
      this.dbZone.revision = result.zoneRevision;
      this.loadedZoneRevision = result.zoneRevision;
      Object.keys(result.floorPlanRevisions).forEach(id => {
        let plan = this.floorPlans.find(item => item.id === id);
        if (plan) plan.revision = result.floorPlanRevisions[id];
      });
      this.captureBaseline();
      this.conflicted = false;
      this.notifier.success('Zone changes saved');
    } catch (e) {
      if (e instanceof services.ZoneRevisionConflictError || e?.name === 'ZoneRevisionConflictError') {
        this.conflicted = true;
        this.notifier.error('Zones changed elsewhere. Reload before saving.');
      } else {
        this.notifier.error('Could not save zone changes. Your edits are still available.');
      }
    }
    this.saving = false;
  }

  revert() {
    if (!this.dirty) return;
    this.notifier.confirm(
      'Undo current edits?',
      'Discard every unsaved zone and floor-plan drawing change from this session?',
      async () => {
        this.dbZone = JSON.parse(this.initialSnapshot);
        this.floorPlans = JSON.parse(this.initialFloorPlansSnapshot);
        this.activeFloorPlan = this.floorPlans.find(plan => plan.id === this.activeFloorPlan?.id) || this.floorPlans[0];
        this.selectedZoneId = null;
        this.creatingZone = false;
        this.pendingShape = null;
        this.conflicted = false;
        if (this.activeFloorPlan) await this.buildStage();
      },
      () => { },
    );
  }

  async reloadAfterConflict() {
    if (!this.conflicted) return;
    this.notifier.confirm(
      'Reload zones?',
      'Reloading discards your unsaved changes and loads the latest version.',
      () => this.load(),
      () => { },
    );
  }

  private captureBaseline() {
    this.initialSnapshot = JSON.stringify(this.dbZone || { zoneTitles: [], list: [], revision: 0 });
    this.initialFloorPlansSnapshot = JSON.stringify(this.floorPlans || []);
  }

  private requireCleanWorkspace(action: string): boolean {
    if (!this.dirty) return true;
    this.notifier.error(`Save or undo current edits before you ${action}.`);
    return false;
  }

  private validRevision(value: number): number {
    return Number.isFinite(value) && value >= 0 ? value : 0;
  }

  private setZonePlacements(list: models.Zone[]) {
    (list || []).forEach(zone => {
      if (zone.zones && zone.zones.length) {
        this.setZonePlacements(zone.zones);
        zone.placements = zone.zones.reduce((total, child) => total + (Number(child.placements) || 0), 0);
      }
    });
  }

  private trimZonesToDepth(list: models.Zone[], depth: number, maximumDepth: number) {
    if (maximumDepth === 0) {
      this.dbZone.list = [];
      return;
    }
    (list || []).forEach(zone => {
      if (depth + 1 >= maximumDepth) zone.zones = [];
      else this.trimZonesToDepth(zone.zones || [], depth + 1, maximumDepth);
    });
  }

  private validateZoneList(list: models.Zone[], depth: number): string {
    if (depth >= (this.dbZone.zoneTitles || []).length && list.length) {
      return 'A zone is deeper than the configured zone levels.';
    }
    let names = new Set<string>();
    for (const zone of list) {
      let name = (zone.name || '').trim();
      if (!name) return 'Every zone must have a name.';
      let key = name.toLowerCase();
      if (names.has(key)) return `Zone names must be unique within the same parent: ${name}.`;
      names.add(key);
      if ((!zone.zones || !zone.zones.length) && (!Number.isFinite(Number(zone.placements)) || Number(zone.placements) < 0)) {
        return `Enter a valid placement capacity for ${name}.`;
      }
      let childError = this.validateZoneList(zone.zones || [], depth + 1);
      if (childError) return childError;
    }
    return null;
  }

  // ------------------------------------------------------------ helpers
  private get imgW(): number { return this.activeFloorPlan?.canvasWidth || this.activeFloorPlan?.imageWidth || 1000; }
  private get imgH(): number { return this.activeFloorPlan?.canvasHeight || this.activeFloorPlan?.imageHeight || 1000; }

  private walkZones(list: models.Zone[], fn: (z: models.Zone) => void) {
    (list || []).forEach(z => { fn(z); if (z.zones) this.walkZones(z.zones, fn); });
  }

  // hex color + alpha -> rgba() string
  private hexA(hex: string, alpha: number): string {
    let h = (hex || DEFAULT_ZONE_COLOR).replace('#', '');
    if (h.length === 3) h = h.split('').map(c => c + c).join('');
    let r = parseInt(h.substring(0, 2), 16);
    let g = parseInt(h.substring(2, 4), 16);
    let b = parseInt(h.substring(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  }
}
